// Global image cache to track which images have been loaded
// This persists across component mounts/unmounts and leverages browser cache

const loadedImages = new Set<string>();
// Track images that are currently loading to prevent parallel loads
const loadingImages = new Map<string, Promise<void>>();

/**
 * Check if an image has been loaded (cached)
 */
export function isImageLoaded(src: string): boolean {
  return loadedImages.has(src);
}

/**
 * Mark an image as loaded
 */
export function markImageAsLoaded(src: string): void {
  loadedImages.add(src);
  // Remove from loading map when done
  loadingImages.delete(src);
}

/**
 * Preload an image and mark it as loaded when complete
 * Prevents parallel loading of the same image
 * Fire-and-forget: does not return a promise
 */
export function preloadImage(src: string): void {
  // If already marked as loaded, do nothing
  if (loadedImages.has(src)) {
    return;
  }

  // If already loading, do nothing (prevents parallel loads)
  if (loadingImages.has(src)) {
    return;
  }

  // Create loading promise and store it
  const loadingPromise = new Promise<void>((resolve, reject) => {
    const img = new Image();
    let isResolved = false;

    const doResolve = () => {
      if (!isResolved) {
        isResolved = true;
        markImageAsLoaded(src);
        resolve();
      }
    };

    img.onload = () => {
      doResolve();
    };

    img.onerror = () => {
      if (!isResolved) {
        isResolved = true;
        // Remove from loading map even on error
        loadingImages.delete(src);
        reject(new Error('Image failed to load'));
      }
    };

    // Set src after handlers are attached
    img.src = `${src}`;

    // Check if image is already complete (cached by browser)
    // This happens synchronously for cached images
    if (img.complete && img.naturalWidth > 0) {
      // Image is already loaded from cache, resolve immediately
      doResolve();
    }
    // If not cached, onload will fire when it loads
  });

  // Store the promise to prevent parallel loads
  loadingImages.set(src, loadingPromise);
}
